import { useResponsive } from 'ahooks'
import { snapshot, useSnapshot } from 'valtio'
import styles from './content.module.less'

import ChatItem from './ChatItem/index'
import ChatTrigger from './ChatTrigger/index'

import { privacyPolicy, userAgreement } from '@/components/Agreement'
import useScrollToBottom from '@/hooks/useScrollToBottom'
import { assistantAction, assistantState } from '@/store'
import { Spin } from 'antd'
import AstInitMsg from '../../widget/AstInitMsg'
import QuestionRecommend from '../../widget/QuestionRecommend'
import ChatRecord from './ChatRecord'
import { useLocation } from 'react-router-dom'
import { cn } from '@/utils/css'
import { useRef, useLayoutEffect, useEffect } from 'react'
import { SessionStatuTypes } from '@/constants'
import { useAgentChat } from '@/hooks/useAgentChat'

export const Footer = () => {
  const responsive = useResponsive()
  return (
    <div className={styles.footer}>
      {responsive.sm ? (
        <>
          <span className={styles.desc}>
            测试产生的所有内容均由人工智能模型输出，其内容的准确性和完整性无法保证，不代表我们的态度或观点.
          </span>
          <span
            className={`${styles.linkItem} ${styles.left}`}
            rel="noopener noreferrer"
            onClick={() => {
              window.open(userAgreement)
            }}
          >
            用户协议
          </span>
          <span
            className={styles.linkItem}
            rel="noopener noreferrer"
            onClick={() => {
              window.open(privacyPolicy)
            }}
          >
            隐私政策
          </span>
        </>
      ) : (
        <span>所有内容均由人工智能模型输出，不代表我们的态度或观点。</span>
      )}
    </div>
  )
}
interface IProps {
  className?: string
  isShowClean?: boolean
}

const ChatContent = ({ className, isShowClean = true }: IProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const userScrolledRef = useRef(false)
  const lastMessagesLengthRef = useRef(0)
  const {
    curSelectAssistant,
    sessionStatu,
    sessionMessages,
    isLoadingPage,
    questionGuides,
    historyRecord,
    autoBottom,
    isLoadingQuestion,
  } = useSnapshot(assistantState)

  // 监听用户滚动
  useEffect(() => {
    // 使用 requestAnimationFrame 确保在下一帧时DOM已经渲染
    const initScroll = () => {
      const dom = scrollRef.current
      if (!dom) {
        requestAnimationFrame(initScroll)
        return
      }

      let lastScrollTop = dom.scrollTop
      const handleScroll = () => {
        if (!dom) return
        const currentScrollTop = dom.scrollTop
        const distanceToBottom =
          dom.scrollHeight - currentScrollTop - dom.clientHeight
        // 检测滚动方向
        const isScrollingUp = currentScrollTop < lastScrollTop
        if (isScrollingUp) {
          userScrolledRef.current = true
          console.log('用户向上滚动', userScrolledRef.current)
        }
        if (distanceToBottom < 10) {
          userScrolledRef.current = false
          console.log('滚动到底部', userScrolledRef.current)
        }
        lastScrollTop = currentScrollTop
      }

      // 使用 passive: true 提高滚动性能
      dom.addEventListener('scroll', handleScroll, { passive: true })
      // 初始化时检查一次滚动位置
      handleScroll()

      return () => {
        dom.removeEventListener('scroll', handleScroll)
      }
    }

    const cleanup = initScroll()
    return () => {
      if (cleanup) cleanup()
    }
  }, []) // 确保只在组件挂载时添加监听器

  // 处理消息更新时的滚动
  useLayoutEffect(() => {
    const dom = scrollRef.current
    if (!dom) return

    // 检查消息长度是否发生变化
    if (sessionMessages.length !== lastMessagesLengthRef.current) {
      console.log(
        '消息长度变化，从',
        lastMessagesLengthRef.current,
        '变为',
        sessionMessages.length,
      )
      // 消息长度发生变化，强制滚动到底部
      dom.scrollTop = dom.scrollHeight
      userScrolledRef.current = false
      // 更新上一次的消息长度
      lastMessagesLengthRef.current = sessionMessages.length
      return
    }

    if (sessionStatu === SessionStatuTypes.pending) {
      console.log('pending状态，当前滚动状态:', userScrolledRef.current)
      // 如果正在响应中，且用户没有手动滚动，则自动滚动到底部
      if (!userScrolledRef.current) {
        dom.scrollTop = dom.scrollHeight
      }
    } else {
      console.log('非pending状态，重置滚动状态')
      // 如果不在响应中，则自动滚动到底部
      dom.scrollTop = dom.scrollHeight
      // 重置用户滚动状态
      userScrolledRef.current = false
    }

    assistantState.autoBottom = false
  }, [sessionMessages, sessionStatu, historyRecord, autoBottom])

  // 销毁时
  useEffect(() => {
    return () => {
      assistantAction.resetCurSelectAssistant({})
    }
  }, [])

  const location = useLocation()
  const { onUserSubmit, isLoading, stopSession } = useAgentChat()
  const responsive = useResponsive()

  return (
    <div className={cn(styles.content, className)}>
      <div
        className={`${styles.cardWrap} ${
          location.pathname === '/assistant/playground' || className
            ? styles.maincardWrap
            : ''
        }`}
      >
        <div className="h-6 absolute inset-0 -top-1 bg-gradient-to-b from-white to-white/50 blur-sm z-10 " />
        <div ref={scrollRef} className={styles.scrollView}>
          {isLoadingPage ? (
            <div className="flex items-center justify-center h-full">
              <Spin />
            </div>
          ) : (
            <>
              <AstInitMsg onUserSubmit={onUserSubmit} />
              {historyRecord.map((item: any, index: number) => {
                return <ChatRecord item={item} key={item.data_id} />
              })}
              {sessionMessages.map((item: any, index: number) => (
                <ChatItem
                  key={index}
                  item={item}
                  isLast={index === sessionMessages.length - 1}
                />
              ))}
              {curSelectAssistant.question_guide &&
                questionGuides.length > 0 && (
                  <QuestionRecommend
                    related={questionGuides}
                    onUserSubmit={onUserSubmit}
                  />
                )}
            </>
          )}
        </div>
        <ChatTrigger
          isShowClean={isShowClean}
          onUserSubmit={onUserSubmit}
          isLoading={isLoading}
          stopSession={stopSession}
        />
      </div>
    </div>
  )
}

export default ChatContent
